import { BoxProps, Button, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { StyledFlex } from 'src/components/StyledFlex';
import TextField from 'src/components/TextField';
import { useRegistrationRabenToken } from 'src/Ghg/hooks';
import { RegistrationRabenTokenSchema } from 'src/Ghg/interfaces';
import { useAppSelector } from 'src/redux-file/hooks';
import { useBranchesWithCountry } from 'src/utils/branches';

type Props = {
  organizationId: number;
} & BoxProps;

export default function CopyLink(props: Props) {
  const { data: tokens } = useRegistrationRabenToken(props.organizationId);
  const { branches } = useBranchesWithCountry();
  const { selectedDate } = useAppSelector((state) => state.questionnaire);
  const selectedYear = String(selectedDate.getFullYear());

  const { t } = useTranslation(undefined, {
    keyPrefix: 'ghg.supplierSurvey.copyLink',
  });

  const tokenUrl = (token: RegistrationRabenTokenSchema) =>
    `${window.location.origin}/suppliers/${token?.token}?reporting_period=${selectedYear}`;

  const copyToClipboard = (token: RegistrationRabenTokenSchema) => {
    navigator.clipboard.writeText(tokenUrl(token));
    toast.success(t('linkCopied') as string);
  };

  // R-18: show branches by translated description, not by raw slug -- and,
  // additive here, prefix it with the branch's country name where one is
  // assigned (a branch can still be in the "no country yet" bucket during
  // rollout, so fall back to the bare description exactly like before).
  const branchLabel = (branchName: string) => {
    const branch = branches.find((b) => b.name === branchName);
    if (!branch) return undefined;
    if (branch.country_name) {
      return t('branchLabelWithCountry', {
        country: branch.country_name,
        branch: branch.description,
      });
    }
    return branch.description;
  };

  if (!tokens) return null;

  return (
    <>
      {tokens.map((token) => (
        <StyledFlex {...props} key={token.token}>
          <Typography width="300px">
            {branchLabel(token.branch_name)}
          </Typography>
          <TextField
            fullWidth
            value={tokenUrl(token)}
            disabled
            label={t('label')}
            sx={{
              mr: '10px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '25px',
              },
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: '#000000',
              },
            }}
            InputLabelProps={{
              shrink: true,
              style: { color: 'black' },
            }}
            InputProps={{
              classes: {
                notchedOutline: 'Black MuiOutlinedInput-notchedOutline',
              },
            }}
          />
          <Button
            onClick={() => copyToClipboard(token)}
            sx={{ minWidth: '200px' }}
            size="large"
          >
            {t('copy')}
          </Button>
        </StyledFlex>
      ))}
    </>
  );
}
